import * as React from 'react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import AccountCircle from '@mui/icons-material/AccountCircle'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { RELEASE } from '../bootstrapping/sentry'
import { copyToClipboard } from '../utils/clipboard'
import { useAuth0 } from '@auth0/auth0-react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelectedCustomer } from '../routes/CustomerSelector'
import AppBarBox from './AppBarBox'

const MenuAppBar: React.FC = () => {
  const navigate = useNavigate()

  const selectedCustomer = useSelectedCustomer()

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const auth0 = useAuth0()

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  // DBS-351: the version copies on click, with a notification (the menu closes, so the notification lives outside it)
  const [versionNotice, setVersionNotice] = React.useState<{ copied: boolean } | null>(null)
  const onClickVersion = async () => {
    handleClose()
    setVersionNotice({ copied: await copyToClipboard(RELEASE) })
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onClickProfile = () => {
    navigate('/profile')
    handleClose()
  }

  const onClickLogout = () => {
    auth0.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
    handleClose()
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" color='primary'>
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div">
            <img src="/branding/totalscreening/logo.jpeg" alt={`${process.env.REACT_APP_NAME_STUB} Logo`} style={{ marginTop: 10, marginRight: 10 }}/>
          </Typography>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Customer Portal
            {selectedCustomer && (
              <span style={{ marginLeft: 3 }}>
              - {selectedCustomer.customer_name}
            </span>
            )}
            <span style={{ marginLeft: '1rem' }}>
              <Link to='/'>🏠</Link>
            </span>
          </Typography>
          <AppBarBox sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }} />
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={onClickProfile}>Profile</MenuItem>
              <MenuItem onClick={onClickLogout}>Logout</MenuItem>
              {/* DBS-350: which build this is, for screenshots and support; DBS-351: click to copy */}
              <MenuItem dense onClick={onClickVersion} data-testid="app-release" title="Copy the version to the clipboard">
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Version {RELEASE.replace(/^fe-[a-z]+@/, '')}
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </Typography>
              </MenuItem>
            </Menu>
            <Snackbar
              open={versionNotice !== null}
              autoHideDuration={versionNotice?.copied === false ? 10000 : 3000}
              onClose={() => setVersionNotice(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert
                severity={versionNotice?.copied === false ? 'warning' : 'success'}
                variant="filled"
                onClose={() => setVersionNotice(null)}
                data-testid="app-release-copied"
              >
                {versionNotice?.copied === false
                  ? `Couldn't copy to the clipboard. The version is ${RELEASE}`
                  : `Version ${RELEASE} copied to the clipboard`}
              </Alert>
            </Snackbar>
          </div>
        </Toolbar>
      </AppBar>
    </Box>
  )
}

export default MenuAppBar
